"use client";

import * as React from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ServiceLocation } from "./ServiceLocationsList";
import { Loader2 } from "lucide-react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { useGoogleMaps } from "@/context/GoogleMapsContext";
import { grayMapStyle } from "@/utils/variables";
import AutocompleteLocation from "@/taxi-dispatch/dispatch/components/AutocompleteLocation";
import axiosInstance from "@/utils/axiosInstance";

interface Location {
  text: string;
  lat: number;
  lng: number;
}

interface ServiceLocationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location?: ServiceLocation | null;
  mode: "create" | "edit";
  onSuccess: () => void;
}

export function ServiceLocationDialog({
  open,
  onOpenChange,
  location,
  mode,
  onSuccess,
}: ServiceLocationDialogProps) {
  const [isCreating, setIsCreating] = React.useState(false);
  const [isUpdating, setIsUpdating] = React.useState(false);

  const [formData, setFormData] = React.useState({
    name: "",
    alias: "",
    type: "POINT" as const,
    coordinates: [] as Array<{ lat: number; lng: number; name?: string }>,
    description: "",
  });

  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [selectedMapPosition, setSelectedMapPosition] = React.useState<{
    lat: number;
    lng: number;
  } | null>(null);
  const nameInputRef = React.useRef<HTMLInputElement>(null);

  const { isLoaded, loadError } = useGoogleMaps();
  const [mapCenter] = React.useState({ lat: 41.3275, lng: 19.8189 });

  const isLoading = isCreating || isUpdating;

  // Reset form when location changes or dialog opens
  React.useEffect(() => {
    if (open) {
      if (mode === "edit" && location) {
        // When editing, check if the name is stored in coordinates (meaning it's an alias)
        const hasLocationNameInCoords =
          location.coordinates.length > 0 && location.coordinates[0].name;
        setFormData({
          name: hasLocationNameInCoords
            ? location.coordinates[0].name || ""
            : location.name,
          alias: hasLocationNameInCoords ? location.name : "",
          type: "POINT",
          coordinates: location.coordinates,
          description: location.description || "",
        });
        // Set coordinates for map display
        if (location.coordinates.length > 0) {
          setSelectedMapPosition(location.coordinates[0]);
        }
        // Set the input value for autocomplete
        if (nameInputRef.current) {
          nameInputRef.current.value = hasLocationNameInCoords
            ? location.coordinates[0].name || ""
            : location.name;
        }
      } else {
        setFormData({
          name: "",
          alias: "",
          type: "POINT",
          coordinates: [],
          description: "",
        });
        setSelectedMapPosition(null);
        // Clear the input value for autocomplete
        if (nameInputRef.current) {
          nameInputRef.current.value = "";
        }
      }
      setErrors({});
    }
  }, [open, mode, location]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Location name is required";
    }
    if (!formData.coordinates || formData.coordinates.length === 0) {
      newErrors.coordinates = "Coordinates are required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      // If alias is set, use alias as name and store original name in coordinates
      // If no alias, use the location name as the service location name
      const finalName = formData.alias.trim() || formData.name.trim();

      // Ensure coordinates always have the location name
      const coordinatesWithName = formData.coordinates.map((coord) => ({
        ...coord,
        name: coord.name || formData.name,
      }));

      const data = {
        name: finalName,
        type: formData.type,
        coordinates: coordinatesWithName,
        description: formData.description.trim() || undefined,
      };

      if (mode === "create") {
        setIsCreating(true);
        await axiosInstance.post("/route-areas", data);
        setIsCreating(false);
        toast("Area created successfully", {
          description: "Your area was added successfully!",
        });
      } else if (mode === "edit" && location) {
        // Use axios directly for PATCH request
        setIsUpdating(true);
        await axiosInstance.patch(`/route-areas/${location.id}`, data);
        setIsUpdating(false);
        toast("Area updated successfully", {
          description: "Your area was updated successfully!",
        });
      }
      onSuccess();
    } catch (error) {
      setIsCreating(false);
      setIsUpdating(false);
      toast(`Error ${mode === "create" ? "creating" : "updating"} area`, {
        description:
          "Please try again or contact support if the problem persists.",
      });
    }
  };

  const handleInputChange = (
    field: string,
    value: string | Array<{ lat: number; lng: number; name?: string }>,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleMapClick = (event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      const newPosition = { lat, lng };
      setSelectedMapPosition(newPosition);

      // Use reverse geocoding to get the location name
      if (window.google?.maps?.Geocoder) {
        const geocoder = new window.google.maps.Geocoder();
        geocoder.geocode({ location: newPosition }, (results, status) => {
          if (status === "OK" && results && results.length > 0) {
            const locationName = results[0].formatted_address;
            handleInputChange("name", locationName);
            handleInputChange("coordinates", [
              { lat, lng, name: locationName },
            ]);
            // Update the autocomplete input value
            if (nameInputRef.current) {
              nameInputRef.current.value = locationName;
            }
          } else {
            // Fallback if geocoding fails
            handleInputChange("coordinates", [{ lat, lng }]);
          }
        });
      } else {
        // Fallback if geocoding is not available
        handleInputChange("coordinates", [{ lat, lng }]);
      }
    }
  };

  const handleLocationSelect = (locationData: Location) => {
    handleInputChange("name", locationData.text);
    const coordinates = [
      { lat: locationData.lat, lng: locationData.lng, name: locationData.text },
    ];
    handleInputChange("coordinates", coordinates);
    setSelectedMapPosition({ lat: locationData.lat, lng: locationData.lng });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>
              {mode === "create" ? "Create Area" : "Edit Area"}
            </DialogTitle>
            <DialogDescription>
              {mode === "create"
                ? "Add a new area to your system."
                : "Update the area details."}
            </DialogDescription>
          </DialogHeader>

          <ScrollArea className="flex-1 pr-4 overflow-auto">
            <form
              id="service-location-form"
              onSubmit={handleSubmit}
              className="space-y-4"
            >
              <div className="flex gap-4">
                <div className="flex-1 space-y-2">
                  <AutocompleteLocation
                    placeholder="Enter location name"
                    location={handleLocationSelect}
                    inputRef={nameInputRef}
                    required={true}
                    selectedLocation={
                      formData.name
                        ? { text: formData.name, lat: 0, lng: 0 }
                        : undefined
                    }
                  />
                  {errors.name && (
                    <p className="text-sm text-red-500">{errors.name}</p>
                  )}
                </div>

                <div className="w-64 space-y-2">
                  <Label htmlFor="alias">Location Alias (Optional)</Label>
                  <Input
                    id="alias"
                    placeholder="Enter custom alias"
                    value={formData.alias}
                    onChange={(e) => handleInputChange("alias", e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="coordinates">Coordinates</Label>
                <div className="space-y-3">
                  {isLoaded && !loadError && (
                    <div className="border rounded-md overflow-hidden">
                      <GoogleMap
                        center={selectedMapPosition || mapCenter}
                        zoom={13}
                        mapContainerClassName="h-[400px] w-full"
                        onClick={handleMapClick}
                        options={{
                          styles: grayMapStyle,
                          fullscreenControl: false,
                          streetViewControl: false,
                          mapTypeControl: false,
                          gestureHandling: "greedy",
                        }}
                      >
                        {selectedMapPosition && (
                          <Marker position={selectedMapPosition} />
                        )}
                      </GoogleMap>
                      <div className="p-2 bg-gray-50 text-sm text-gray-600">
                        Click on the map to select a location
                        {selectedMapPosition && (
                          <span className="ml-2 font-mono">
                            ({selectedMapPosition.lat.toFixed(6)},{" "}
                            {selectedMapPosition.lng.toFixed(6)})
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {loadError && (
                    <div className="p-4 bg-red-50 border border-red-200 rounded-md">
                      <p className="text-red-600">
                        Error loading map: {loadError.message}
                      </p>
                    </div>
                  )}

                  {!isLoaded && (
                    <div className="p-4 bg-gray-50 border rounded-md">
                      <p className="text-gray-600">Loading map...</p>
                    </div>
                  )}
                </div>
                {errors.coordinates && (
                  <p className="text-sm text-red-500">{errors.coordinates}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description (Optional)</Label>
                <Textarea
                  id="description"
                  placeholder="Enter description"
                  className="resize-none"
                  value={formData.description}
                  onChange={(e) =>
                    handleInputChange("description", e.target.value)
                  }
                />
              </div>
            </form>
          </ScrollArea>

          <DialogFooter className="mt-4 flex-shrink-0">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              form="service-location-form"
              disabled={isLoading}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {mode === "create" ? "Create" : "Update"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
